import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import InstallPrompt from "@/components/InstallPrompt";
import { registerServiceWorker } from "@/lib/registerSW";
import { startSyncManager } from "@/lib/syncManager";
import Login from "@/pages/Login";
import SetPassword from "@/pages/SetPassword";
import Dashboard from "@/pages/Dashboard";
import DirectionPanel from "@/pages/DirectionPanel";
import Purchasing from "@/pages/Purchasing";
import TreasuryBanks from "@/pages/TreasuryBanks";
import VendorProfile from "@/pages/VendorProfile";
import Projects from "@/pages/Projects";
import Expenses from "@/pages/Expenses";
import ProjectExpenses from "@/pages/ProjectExpenses";
import BillingPending from "@/pages/BillingPending";
import Invoicing from "@/pages/Invoicing";
import Payroll from "@/pages/Payroll";
import PayrollReceipt from "@/pages/PayrollReceipt";
import ProjectReport from "@/pages/ProjectReport";
import GlobalReport from "@/pages/GlobalReport";
import Vendors from "@/pages/Vendors";
import Investors from "@/pages/Investors";
import Loans from "@/pages/Loans";
import ProjectBudget from "@/pages/ProjectBudget";
import ProjectProgressMobile from "@/pages/ProjectProgressMobile";
import Pieceworkers from "@/pages/Pieceworkers";
import Warehouses from "@/pages/Warehouses";
import ObraControl from "@/pages/ObraControl";
import Users from "@/pages/Users";
import AuditLogs from "@/pages/AuditLogs";
import ContractingEntities from "@/pages/ContractingEntities";
import ClientDetail from "@/pages/ClientDetail";
import ClientsRanking from "@/pages/ClientsRanking";
import Contracts, { ContractForm, ContractDetail } from "@/pages/Contracts";
import SatSync from "@/pages/SatSync";
import Companies from "@/pages/Companies";
import ProjectDashboard from "@/pages/ProjectDashboard";
import Machinery from "@/pages/Machinery";
import Fleet from "@/pages/Fleet";
import FilesGallery from "@/pages/FilesGallery";
import MachineryReport from "@/pages/MachineryReport";
import PublicMachineryReport from "@/pages/PublicMachineryReport";
import PublicPayrollReceipt from "@/pages/PublicPayrollReceipt";
import PublicPieceworkerStatement from "@/pages/PublicPieceworkerStatement";
import PublicPieceworkReceipt from "@/pages/PublicPieceworkReceipt";
import PublicGalleryUpload from "@/pages/PublicGalleryUpload";
import PublicWarehouseEvidence from "@/pages/PublicWarehouseEvidence";
import Gallery from "@/pages/Gallery";
import Workshop from "@/pages/Workshop";
import CashBox from "@/pages/CashBox";
import StaffContributions from "@/pages/StaffContributions";
import WorkshopQuote from "@/pages/WorkshopQuote";
import PublicWorkshopReport from "@/pages/PublicWorkshopReport";
import PublicWorkshopQuote from "@/pages/PublicWorkshopQuote";

registerServiceWorker();
startSyncManager();

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" />
          <InstallPrompt />
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/firma/maquinaria/:token" element={<PublicMachineryReport />} />
            <Route path="/firma/nomina/:token" element={<PublicPayrollReceipt />} />
            <Route path="/firma/destajo/:token" element={<PublicPieceworkerStatement />} />
            <Route path="/firma/recibo-destajo/:token" element={<PublicPieceworkReceipt />} />
            <Route path="/galeria/:token" element={<PublicGalleryUpload />} />
            <Route path="/almacen/:token" element={<PublicWarehouseEvidence />} />
            <Route path="/taller/:token" element={<PublicWorkshopReport />} />
            <Route path="/cotizacion/:token" element={<PublicWorkshopQuote />} />
            <Route path="/workshop/quotes/:id" element={
              <ProtectedRoute roles={["admin", "contador", "auxiliar", "mecanico"]}><WorkshopQuote /></ProtectedRoute>
            } />
            <Route path="/machinery/reports/:id" element={
              <ProtectedRoute roles={["admin", "contador", "auxiliar", "residente", "supervisor"]}><MachineryReport /></ProtectedRoute>
            } />
            <Route path="/payroll/:id/receipt" element={
              <ProtectedRoute roles={["admin", "contador", "auxiliar", "residente"]}><PayrollReceipt /></ProtectedRoute>
            } />
            <Route path="/projects/:id/report" element={
            <ProtectedRoute roles={["admin", "contador"]}><ProjectReport /></ProtectedRoute>
            } />
            <Route path="/reports/global" element={
              <ProtectedRoute roles={["admin", "contador"]}><GlobalReport /></ProtectedRoute>
            } />
            <Route path="/projects/:id/progress-mobile" element={
              <ProtectedRoute roles={["admin", "contador", "supervisor", "residente"]}><ProjectProgressMobile /></ProtectedRoute>
            } />
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route path="/dashboard" element={
                <ProtectedRoute roles={["admin", "contador", "supervisor", "residente", "auxiliar"]}><Dashboard /></ProtectedRoute>
              } />
              <Route path="/direction" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar", "residente", "supervisor"]}><DirectionPanel /></ProtectedRoute>
              } />
              <Route path="/treasury-banks" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar"]}><TreasuryBanks /></ProtectedRoute>
              } />
              <Route path="/vendors/:id" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar"]}><VendorProfile /></ProtectedRoute>
              } />
              <Route path="/purchasing" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar", "residente", "supervisor"]}><Purchasing /></ProtectedRoute>
              } />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:id" element={<ProjectDashboard />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/projects/:id/gallery" element={<Gallery />} />
              <Route path="/projects/:id/machinery" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar", "residente", "supervisor"]}><Machinery /></ProtectedRoute>
              } />
              <Route path="/projects/:id/contracts" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar"]}><Contracts /></ProtectedRoute>
              } />
              <Route path="/machinery" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar", "residente", "supervisor"]}><Machinery /></ProtectedRoute>
              } />
              <Route path="/fleet" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar", "residente", "supervisor"]}><Fleet /></ProtectedRoute>
              } />
              <Route path="/files-gallery" element={
                <ProtectedRoute roles={["admin", "contador"]}><FilesGallery /></ProtectedRoute>
              } />
              <Route path="/workshop" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar", "mecanico"]}><Workshop /></ProtectedRoute>
              } />
              <Route path="/cash" element={
                <ProtectedRoute roles={["admin", "auxiliar", "residente", "supervisor", "contador"]}><CashBox /></ProtectedRoute>
              } />
              <Route path="/staff-contributions" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar"]}><StaffContributions /></ProtectedRoute>
              } />
              <Route path="/expenses" element={<Expenses />} />
              <Route path="/payroll" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar", "residente", "supervisor"]}><Payroll /></ProtectedRoute>
              } />
              <Route path="/vendors" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar"]}><Vendors /></ProtectedRoute>
              } />
              <Route path="/investors" element={
                <ProtectedRoute roles={["admin", "contador"]}><Investors /></ProtectedRoute>
              } />
              <Route path="/loans" element={
                <ProtectedRoute roles={["admin", "contador"]}><Loans /></ProtectedRoute>
              } />
              <Route path="/projects/:id/budget" element={
                <ProtectedRoute roles={["admin", "contador", "supervisor", "residente", "auxiliar"]}><ProjectBudget /></ProtectedRoute>
              } />
              <Route path="/projects/:id/expenses" element={
                <ProtectedRoute roles={["admin", "contador", "supervisor", "residente"]}><ProjectExpenses /></ProtectedRoute>
              } />
              <Route path="/billing/pending-invoices" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar"]}><BillingPending /></ProtectedRoute>
              } />
              <Route path="/invoicing" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar"]}><Invoicing /></ProtectedRoute>
              } />
              <Route path="/sat" element={
                <ProtectedRoute roles={["admin", "contador"]}><SatSync /></ProtectedRoute>
              } />
              <Route path="/pieceworkers" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar", "residente"]}><Pieceworkers /></ProtectedRoute>
              } />
              <Route path="/warehouses" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar", "residente", "supervisor"]}><Warehouses /></ProtectedRoute>
              } />
              <Route path="/control-obra" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar", "residente", "supervisor"]}><ObraControl /></ProtectedRoute>
              } />
              <Route path="/users" element={
                <ProtectedRoute roles={["admin"]}><Users /></ProtectedRoute>
              } />
              <Route path="/companies" element={
                <ProtectedRoute roles={["admin"]}><Companies /></ProtectedRoute>
              } />
              <Route path="/audit-logs" element={
                <ProtectedRoute roles={["admin", "contador"]}><AuditLogs /></ProtectedRoute>
              } />
              <Route path="/contracting-entities" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar"]}><ContractingEntities /></ProtectedRoute>
              } />
              <Route path="/contracting-entities/ranking" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar"]}><ClientsRanking /></ProtectedRoute>
              } />
              <Route path="/contracting-entities/:id" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar"]}><ClientDetail /></ProtectedRoute>
              } />
              <Route path="/contracts" element={
                <ProtectedRoute><Contracts /></ProtectedRoute>
              } />
              <Route path="/contracts/new" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar"]}><ContractForm /></ProtectedRoute>
              } />
              <Route path="/contracts/:id/edit" element={
                <ProtectedRoute roles={["admin", "contador", "auxiliar"]}><ContractForm /></ProtectedRoute>
              } />
              <Route path="/contracts/:id" element={
                <ProtectedRoute><ContractDetail /></ProtectedRoute>
              } />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
